import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { ArrowRight, ArrowLeft, Loader2, ShieldAlert, Check } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const STEPS = [
  { key: "parties", label: "The Parties" },
  { key: "marriage", label: "Marriage & Grounds" },
  { key: "children", label: "Children" },
  { key: "indigency", label: "Indigency" },
  { key: "settlement", label: "Settlement & Service" },
];

const NY_COUNTIES = [
  "Albany","Bronx","Brooklyn (Kings)","Buffalo (Erie)","Manhattan (New York)","Nassau","Queens","Rockland","Staten Island (Richmond)","Suffolk","Westchester","Other",
];

const INITIAL = {
  petitioner_name: "", petitioner_address: "",
  respondent_name: "", respondent_address: "",
  county: "", index_number: "",
  marriage_date: "", marriage_location: "",
  residency_party: "Petitioner", residency_years: "two",
  breakdown_date: "", no_reconciliation: "yes",
  has_children: "no", children_count: "", custody_proposed: "", current_living: "",
  claiming_indigency: "no", monthly_income: "", incarcerated: "no", financial_hardship: "",
  service_method: "Personal Service",
  settlement_property: "", settlement_support: "", relief_requested: "",
  email: "",
};

const STEP_CIRCLE = {
  done: "bg-blue-700 border-amber-400 text-white",
  active: "border-amber-400 text-amber-400",
  todo: "border-zinc-700 text-zinc-500",
};

function getStepStateClass(done, active) {
  if (done) return STEP_CIRCLE.done;
  if (active) return STEP_CIRCLE.active;
  return STEP_CIRCLE.todo;
}

function renderSubmitLabel(submitting, isLast) {
  if (submitting) return <Loader2 className="h-4 w-4 animate-spin" />;
  const text = isLast ? "Continue to Payment" : "Next";
  return <>{text} <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} /></>;
}

export default function IntakeDivorce() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isLast = step === STEPS.length - 1;
  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const validateCurrent = () => {
    switch (STEPS[step].key) {
      case "parties": return form.petitioner_name && form.petitioner_address && form.respondent_name && form.county;
      case "marriage": return form.marriage_date && form.marriage_location && form.breakdown_date;
      case "children": return form.has_children === "no" || (form.children_count && form.custody_proposed);
      case "indigency": return form.claiming_indigency === "no" || form.monthly_income;
      case "settlement": return form.email;
      default: return true;
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validateCurrent()) { toast.error("Please complete the required fields on this step."); return; }
    if (!isLast) { next(); return; }
    setSubmitting(true);
    try {
      const { data: intake } = await api.post("/intake/divorce", form);
      const { data: checkout } = await api.post("/checkout/session", {
        product_id: "divorce_packet",
        intake_id: intake.id,
        email: form.email,
        origin_url: window.location.origin,
      });
      window.location.href = checkout.url;
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-black text-zinc-100 min-h-screen pt-12 pb-24">
      <div className="container-pl max-w-2xl">
        <p className="overline mb-4">Divorce Packet (NY) · $99.99</p>
        <h1 className="display-serif text-4xl sm:text-5xl text-zinc-50 leading-tight mb-3 accent-rule">
          Divorce Packet Intake
        </h1>
        <p className="text-base text-zinc-400 mb-8 max-w-xl leading-relaxed">
          A five-step intake that builds a court-ready uncontested divorce packet aligned with NY Supreme Court formatting.
        </p>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-8" data-testid="divorce-step-indicator">
          {STEPS.map((s, i) => {
            const active = i === step;
            const done = i < step;
            return (
              <div key={s.key} className="flex items-center flex-1">
                <div className={`h-7 w-7 rounded-full border flex items-center justify-center text-xs font-mono ${getStepStateClass(done, active)}`}>
                  {done ? <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> : i + 1}
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-1 ${done ? "bg-blue-700" : "bg-zinc-800"}`}/>}
              </div>
            );
          })}
        </div>
        <p className="text-xs uppercase tracking-[0.18em] text-amber-400 mb-6">{STEPS[step].label}</p>

        <div className="border border-amber-400/30 bg-blue-950/20 rounded-sm p-4 mb-6 flex gap-3">
          <ShieldAlert className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <p className="text-xs text-zinc-300 leading-relaxed">
            We are not attorneys and do not provide legal advice. This packet is administrative document preparation only and does not substitute for counsel.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="divorce-intake-form">
          {STEPS[step].key === "parties" && (
            <>
              <h2 className="font-mono text-xs tracking-[0.18em] uppercase text-zinc-500 mt-1 mb-2">Petitioner (Plaintiff)</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-zinc-300">Full Legal Name <span className="text-amber-400">*</span></Label><Input required value={form.petitioner_name} onChange={(e) => update("petitioner_name", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="d-pet-name"/></div>
                <div><Label className="text-zinc-300">County <span className="text-amber-400">*</span></Label>
                  <Select value={form.county} onValueChange={(v) => update("county", v)} required>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1.5 text-zinc-100" data-testid="d-county"><SelectValue placeholder="Choose county"/></SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                      {NY_COUNTIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2"><Label className="text-zinc-300">Petitioner Address <span className="text-amber-400">*</span></Label><Input required value={form.petitioner_address} onChange={(e) => update("petitioner_address", e.target.value)} placeholder="Street, City, State, ZIP" className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="d-pet-addr"/></div>
              </div>

              <h2 className="font-mono text-xs tracking-[0.18em] uppercase text-zinc-500 mt-6 mb-2">Respondent (Defendant)</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-zinc-300">Full Legal Name <span className="text-amber-400">*</span></Label><Input required value={form.respondent_name} onChange={(e) => update("respondent_name", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="d-resp-name"/></div>
                <div><Label className="text-zinc-300">Index Number (if known)</Label><Input value={form.index_number} onChange={(e) => update("index_number", e.target.value)} placeholder="Leave blank if unfiled" className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="d-index"/></div>
                <div className="sm:col-span-2"><Label className="text-zinc-300">Respondent Address (if known)</Label><Input value={form.respondent_address} onChange={(e) => update("respondent_address", e.target.value)} placeholder="Street, City, State, ZIP" className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="d-resp-addr"/></div>
              </div>
            </>
          )}

          {STEPS[step].key === "marriage" && (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-zinc-300">Date of Marriage <span className="text-amber-400">*</span></Label><Input type="date" required value={form.marriage_date} onChange={(e) => update("marriage_date", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="d-marriage-date"/></div>
                <div><Label className="text-zinc-300">Location of Marriage <span className="text-amber-400">*</span></Label><Input required value={form.marriage_location} onChange={(e) => update("marriage_location", e.target.value)} placeholder="City, State (Country)" className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="d-marriage-loc"/></div>
              </div>

              <div className="border border-zinc-800 rounded-sm p-4 bg-zinc-950/40">
                <p className="text-xs uppercase tracking-[0.18em] text-amber-400 mb-3">NY Residency (DRL §230)</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label className="text-zinc-300">Which party meets residency?</Label>
                    <Select value={form.residency_party} onValueChange={(v) => update("residency_party", v)}>
                      <SelectTrigger className="bg-black border-zinc-800 mt-1.5 text-zinc-100" data-testid="d-residency-party"><SelectValue/></SelectTrigger>
                      <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                        <SelectItem value="Petitioner">Petitioner</SelectItem>
                        <SelectItem value="Defendant">Defendant</SelectItem>
                        <SelectItem value="Both Parties">Both Parties</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-zinc-300">Years in NY (continuous)</Label>
                    <Select value={form.residency_years} onValueChange={(v) => update("residency_years", v)}>
                      <SelectTrigger className="bg-black border-zinc-800 mt-1.5 text-zinc-100" data-testid="d-residency-years"><SelectValue/></SelectTrigger>
                      <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                        <SelectItem value="one">1 year</SelectItem>
                        <SelectItem value="two">2 years</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div><Label className="text-zinc-300">Date Marriage Broke Down Irretrievably <span className="text-amber-400">*</span></Label><Input type="date" required value={form.breakdown_date} onChange={(e) => update("breakdown_date", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="d-breakdown"/>
                <p className="text-[11px] text-zinc-500 mt-1.5">Must be at least 6 months prior to filing (DRL §170(7)).</p>
              </div>
              <div><Label className="text-zinc-300 mb-2 block">Reconciliation prospects?</Label>
                <RadioGroup value={form.no_reconciliation} onValueChange={(v) => update("no_reconciliation", v)} className="flex gap-4">
                  <Label className="flex items-center gap-2 border border-zinc-800 rounded-sm px-4 py-2 cursor-pointer hover:border-amber-400/50"><RadioGroupItem value="yes" className="border-amber-400/60 text-amber-400"/><span className="text-sm">No reasonable prospect</span></Label>
                  <Label className="flex items-center gap-2 border border-zinc-800 rounded-sm px-4 py-2 cursor-pointer hover:border-amber-400/50"><RadioGroupItem value="no" className="border-amber-400/60 text-amber-400"/><span className="text-sm">Efforts attempted</span></Label>
                </RadioGroup>
              </div>
            </>
          )}

          {STEPS[step].key === "children" && (
            <>
              <div><Label className="text-zinc-300 mb-2 block">Are there unemancipated children of the marriage?</Label>
                <RadioGroup value={form.has_children} onValueChange={(v) => update("has_children", v)} className="flex gap-4">
                  <Label className="flex items-center gap-2 border border-zinc-800 rounded-sm px-4 py-2 cursor-pointer hover:border-amber-400/50"><RadioGroupItem value="no" className="border-amber-400/60 text-amber-400"/><span className="text-sm">No</span></Label>
                  <Label className="flex items-center gap-2 border border-zinc-800 rounded-sm px-4 py-2 cursor-pointer hover:border-amber-400/50"><RadioGroupItem value="yes" className="border-amber-400/60 text-amber-400"/><span className="text-sm">Yes</span></Label>
                </RadioGroup>
              </div>
              {form.has_children === "yes" && (
                <>
                  <div><Label className="text-zinc-300">Number of children <span className="text-amber-400">*</span></Label><Input required type="number" min="1" value={form.children_count} onChange={(e) => update("children_count", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5 w-32" data-testid="d-children-count"/></div>
                  <div><Label className="text-zinc-300">Current Living Arrangement</Label><Textarea rows={3} value={form.current_living} onChange={(e) => update("current_living", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="d-current-living" placeholder="With whom do the children currently reside? Where?"/></div>
                  <div><Label className="text-zinc-300">Proposed Custody Arrangement <span className="text-amber-400">*</span></Label><Textarea required rows={4} value={form.custody_proposed} onChange={(e) => update("custody_proposed", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="d-custody" placeholder="e.g., Joint legal custody; primary residential custody with petitioner; defendant parenting time alternate weekends..."/>
                    <p className="text-[11px] text-zinc-500 mt-1.5">Best-interests-of-the-child language will be added automatically per DRL §240.</p>
                  </div>
                </>
              )}
            </>
          )}

          {STEPS[step].key === "indigency" && (
            <>
              <div><Label className="text-zinc-300 mb-2 block">Apply to proceed as a poor person? (CPLR Art. 11)</Label>
                <RadioGroup value={form.claiming_indigency} onValueChange={(v) => update("claiming_indigency", v)} className="flex gap-4">
                  <Label className="flex items-center gap-2 border border-zinc-800 rounded-sm px-4 py-2 cursor-pointer hover:border-amber-400/50"><RadioGroupItem value="no" className="border-amber-400/60 text-amber-400"/><span className="text-sm">No</span></Label>
                  <Label className="flex items-center gap-2 border border-zinc-800 rounded-sm px-4 py-2 cursor-pointer hover:border-amber-400/50"><RadioGroupItem value="yes" className="border-amber-400/60 text-amber-400"/><span className="text-sm">Yes — waive filing fee</span></Label>
                </RadioGroup>
              </div>
              {form.claiming_indigency === "yes" && (
                <>
                  <div><Label className="text-zinc-300">Monthly Income (approx.) <span className="text-amber-400">*</span></Label><Input required value={form.monthly_income} onChange={(e) => update("monthly_income", e.target.value)} placeholder="$" className="bg-zinc-950 border-zinc-800 mt-1.5 w-40" data-testid="d-income"/></div>
                  <div><Label className="text-zinc-300 mb-2 block">Are you currently incarcerated?</Label>
                    <RadioGroup value={form.incarcerated} onValueChange={(v) => update("incarcerated", v)} className="flex gap-4">
                      <Label className="flex items-center gap-2 border border-zinc-800 rounded-sm px-4 py-2 cursor-pointer hover:border-amber-400/50"><RadioGroupItem value="no" className="border-amber-400/60 text-amber-400"/><span className="text-sm">No</span></Label>
                      <Label className="flex items-center gap-2 border border-zinc-800 rounded-sm px-4 py-2 cursor-pointer hover:border-amber-400/50"><RadioGroupItem value="yes" className="border-amber-400/60 text-amber-400"/><span className="text-sm">Yes</span></Label>
                    </RadioGroup>
                  </div>
                  <div><Label className="text-zinc-300">Brief description of financial hardship</Label><Textarea rows={3} value={form.financial_hardship} onChange={(e) => update("financial_hardship", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="d-hardship"/></div>
                </>
              )}
            </>
          )}

          {STEPS[step].key === "settlement" && (
            <>
              <div><Label className="text-zinc-300">Method of Service</Label>
                <Select value={form.service_method} onValueChange={(v) => update("service_method", v)}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1.5 text-zinc-100" data-testid="d-service-method"><SelectValue/></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    <SelectItem value="Personal Service">Personal Service</SelectItem>
                    <SelectItem value="Service by Mail">Service by Mail</SelectItem>
                    <SelectItem value="Service by Publication">Service by Publication</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-zinc-500 mt-1.5">Per CPLR §308. Must be completed within 120 days of filing (CPLR §306-b).</p>
              </div>

              <div><Label className="text-zinc-300">Property / Settlement Terms</Label><Textarea rows={3} value={form.settlement_property} onChange={(e) => update("settlement_property", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="d-property" placeholder="e.g., Each party retains personal property in their possession; marital property to be divided equitably..."/></div>
              <div><Label className="text-zinc-300">Support / Maintenance Terms</Label><Textarea rows={3} value={form.settlement_support} onChange={(e) => update("settlement_support", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="d-support" placeholder="e.g., Each party waives spousal maintenance; child support per CSSA guidelines..."/></div>
              <div><Label className="text-zinc-300">Specific Relief Requested</Label><Textarea rows={2} value={form.relief_requested} onChange={(e) => update("relief_requested", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="d-relief" placeholder="Optional — leave blank for standard divorce relief language."/></div>

              <div className="pt-2"><Label className="text-zinc-300">Delivery Email (for the finished PDF) <span className="text-amber-400">*</span></Label><Input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="d-email"/></div>
            </>
          )}

          <div className="flex items-center justify-between pt-6 border-t border-zinc-800">
            {step > 0 ? (
              <Button type="button" variant="outline" onClick={back} className="rounded-sm border-zinc-700 text-zinc-300 hover:bg-zinc-900" data-testid="d-back">
                <ArrowLeft className="mr-2 h-4 w-4" strokeWidth={1.5} /> Back
              </Button>
            ) : <Link to="/services" className="text-sm text-zinc-400 hover:text-amber-400 transition-colors">← Back to services</Link>}

            <Button type="submit" size="lg" disabled={submitting} className="rounded-sm bg-blue-700 hover:bg-blue-600 text-white" data-testid="d-next">
              {renderSubmitLabel(submitting, isLast)}
            </Button>
          </div>
          {isLast && (
            <div className="pt-2">
              <p className="display-serif text-2xl text-amber-400">$99.99 <span className="text-xs text-zinc-500 font-mono">USD · One-time</span></p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
